import { IBaseService } from '@/bundles/App/interfaces';
import AdCampaignItemToMapper from '@/bundles/Ad/mappers/AdCampaignItemToMapper';
import ErrorMapper from '@/bundles/Http/mappers/ErrorMapper';
import HttpTransport from '@/bundles/Http/transport/HttpTransport';
import { PaginationResponse } from '@/bundles/App/type';
import AdCampaignItem from '@/bundles/Ad/models/AdCampaignItem';
import { IGetCampaignItemsDto, IUnassignAdItemDto, IUpdateAdItemInfoDto } from '@/bundles/Ad/dto';
import { IAdCampaignItem } from '@/bundles/Ad/interfaces';

export default class AdCampaignItemService implements IBaseService<AdCampaignItemToMapper> {
  errorMapper: ErrorMapper;
  recordMapper: AdCampaignItemToMapper;
  transport: HttpTransport;

  constructor (props: IBaseService<AdCampaignItemToMapper>) {
    this.errorMapper = props.errorMapper;
    this.recordMapper = props.recordMapper!;
    this.transport = props.transport;
  }

  async getItems (params: IGetCampaignItemsDto): Promise<PaginationResponse<AdCampaignItem>> {
    try {
      const response = await this.transport.get('/ad-campaign/campaign-items', params);
      return {
        data: response.data.map((item: IAdCampaignItem) => this.recordMapper.mapToModel(item)),
        total: response.total,
      };
    } catch (error) {
      throw this.errorMapper.mapToModel(error);
    }
  }

  async exportItems (params: IGetCampaignItemsDto): Promise<string> {
    try {
      const response = await this.transport.get('/ad-campaign/stats', params);
      const blob = new Blob([new Uint8Array(response.data)], {
        type: 'application/vnd.ms-excel',
      });
      return URL.createObjectURL(blob);
    } catch (error) {
      throw this.errorMapper.mapToModel(error);
    }
  }

  async update (id: number, payload: IUpdateAdItemInfoDto) {
    try {
      const formData = new FormData();
      formData.append('url', payload.url);
      if (payload.media_type) {
        formData.append('media_type', payload.media_type);
      }
      if (payload.media?.length) {
        for (const item of payload.media) {
          formData.append('media', item);
        }
      }
      const response = await this.transport.patch(`/ad-campaign/${id}/update-info`, formData, { 'content-type': 'multipart/form-data' });
      return this.recordMapper.mapToModel(response);
    } catch (error) {
      throw this.errorMapper.mapToModel(error);
    }
  }

  async assignAdItem (id: number, payload: IUpdateAdItemInfoDto) {
    try {
      const formData = new FormData();
      formData.append('url', payload.url);
      formData.append('media_type', payload.media_type!);
      if (payload.media?.length) {
        for (const item of payload.media) {
          formData.append('media', item);
        }
      }
      await this.transport.post(`/ad-campaign/${id}/assign-item`, formData, { 'content-type': 'multipart/form-data' });
    } catch (error) {
      throw this.errorMapper.mapToModel(error);
    }
  }

  async unassignAdItems (id: number, payload: IUnassignAdItemDto) {
    try {
      await this.transport.post(`/ad-campaign/${id}/unassign-items`, payload);
    } catch (error) {
      throw this.errorMapper.mapToModel(error);
    }
  }

  async bulkUpdateOrder (payload: { items: Array<{ id: number; order: number }> }) {
    try {
      await this.transport.patch('/ad-campaign/bulk-update-order', payload);
    } catch (error) {
      throw this.errorMapper.mapToModel(error);
    }
  }
}
